import * as React from "react";
import { FileFieldSchema, IInputFieldProps } from "@generation/core";
import { Button, InputFieldProps } from "@shared/common";
import { withHideOnFieldHoc } from "../Hoc/withHideOnFieldHoc";
import { withRegisterFieldHoc } from "../Hoc/withRegisterFieldHoc";
import { withRenderFieldHoc } from "../Hoc/withRenderFieldHoc";
import { useFormContext } from "react-hook-form";
import { FileRow, FileRowModel } from "../Components/File/FileRow";
import "./FileField.scss";
import { withValueSetHoc } from "../Hoc/withValueSetHoc";
import { onChangeHoc } from "../Hoc/onChangeHoc";
import { convertCallbackToValidationArray } from "../Utils/validate";

const RegisteredFileField = withRegisterFieldHoc(
  withRenderFieldHoc((props: IInputFieldProps<FileFieldSchema> & InputFieldProps) => {
    const fileUploadRef = React.useRef<HTMLInputElement | null>(null);
    const { getValues, setValue } = useFormContext();
    const [files, setFiles] = React.useState<File[]>([]);

    React.useEffect(() => {
      const currentFiles = getValues([props.name]);
      if (currentFiles && currentFiles.length > 0 && currentFiles[0]) {
        setFiles(Array.from(currentFiles[0]));
      }
    }, []);

    const onClick = () => {
      if (fileUploadRef.current) {
        fileUploadRef.current.click();
      }
    };

    const onChange = (event) => {
      const fileArray: File[] = event.target.files;

      updateFiles(props.field.multiple === true ? [...files, ...fileArray] : [...fileArray]);
    };

    const onDelete = (file: FileRowModel) => {
      updateFiles(files.filter((item) => item !== file.file));
    };

    const updateFiles = (newFiles: File[]) => {
      setFiles(newFiles);
      setValue(props.name, createFileList(newFiles));
    };

    const createFileList = (files: File[]) => {
      const b = new DataTransfer();
      for (let i = 0, len = files.length; i < len; i++) {
        b.items.add(files[i]);
      }
      return b.files;
    };

    const canAddFiles = props.field.maxFiles === undefined || files.length < props.field.maxFiles;

    return (
      <>
        <input
          className="form-control"
          ref={(ref) => {
            fileUploadRef.current = ref;
            props.inputRef && props.inputRef(ref);
          }}
          type="file"
          multiple={props.field.multiple}
          id={props.name}
          name={props.name}
          onChange={onChange}
          value={props.value}
          accept={props.field.acceptFileTypes}
        />
        {canAddFiles && (
          <Button id="choose-files" buttonText={props.field.buttonText ?? ""} clickHandler={onClick} />
        )}
        <div className="file-list">
          {files.map((file, idx) => (
            <FileRow<FileRowModel>
              key={idx}
              index={idx}
              file={{ file }}
              validate={convertCallbackToValidationArray(props.field.validate)}
              onDelete={onDelete}
              onDownload={props.field.onDownload}
              fileIndicatorLabel={props.field.fileIndicatorLabel}
              showValidationMessageBelowFile={props.field.showValidationMessageBelowFile}
            />
          ))}
        </div>
      </>
    );
  })
);

export const FileField = React.memo(
  onChangeHoc(
    withValueSetHoc(
      withHideOnFieldHoc((props: IInputFieldProps<FileFieldSchema>) => (
        <RegisteredFileField {...props} displayStyle="block" />
      ))
    )
  )
);
